import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Trash2, GripVertical } from 'lucide-react';
import { Template, TEMPLATE_COLORS, TEMPLATE_ICONS } from './types';

interface SortableTemplateCardProps {
  template: Template;
  onApply: (template: Template) => void;
  onEdit: (template: Template) => void;
  onDelete: (id: string) => void;
}

export function SortableTemplateCard({ template, onApply, onEdit, onDelete }: SortableTemplateCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: template.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const colorObj = TEMPLATE_COLORS.find(c => c.id === template.header_color) || TEMPLATE_COLORS[0];
  const iconObj = TEMPLATE_ICONS.find(i => i.id === template.icon_id);

  return (
    <Card ref={setNodeRef} style={style} className="overflow-hidden">
      {/* Template preview header */}
      <div 
        className="p-4 flex items-center gap-3"
        style={{ background: `linear-gradient(135deg, ${colorObj.color}, ${colorObj.color}dd)` }}
      >
        {/* Drag handle */}
        <div 
          {...attributes} 
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 -ml-1 hover:bg-white/20 rounded"
        >
          <GripVertical className="w-5 h-5 text-white/70" />
        </div>
        {iconObj && iconObj.icon !== '—' && (
          <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-xl">
            {iconObj.icon}
          </div>
        )}
        <div className="flex-1">
          <h3 className="font-semibold text-white">{template.name}</h3>
          <p className="text-white/80 text-sm">{template.subject}</p>
        </div>
      </div>
      <CardContent className="p-4 flex items-center justify-between bg-slate-50">
        <p className="text-sm text-slate-600 truncate flex-1 mr-4">
          {template.body_html?.substring(0, 100).replace(/<[^>]*>/g, '')}...
        </p>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="default" size="sm" onClick={() => onApply(template)}>
            Använd
          </Button>
          <Button variant="outline" size="sm" onClick={() => onEdit(template)}>
            Redigera
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(template.id)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
